import { RefreshCcw02, XClose } from '@untitledui/icons';
import { createPortal } from 'react-dom';

import { StackingLevelProvider } from '@/contexts/StackingContext';
import { Button } from '@/lib/base/buttons/button';
import { timeParts, useOrgTimeFormat } from '@/lib/time';

import type {
  GmailSendPayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import EmailActionPreview from './artifacts/EmailActionPreview';

interface VersionPreviewModalProps {
  action: ProposedAction<GmailSendPayload>;
  proposalId: number;
  isOpen: boolean;
  onClose: () => void;
  onRestore?: (action: ProposedAction<GmailSendPayload>) => void;
  isLatest: boolean;
  orgId: number;
}

export default function VersionPreviewModal({
  action,
  proposalId,
  isOpen,
  onClose,
  onRestore,
  isLatest,
  orgId,
}: VersionPreviewModalProps) {
  const { timeFormat } = useOrgTimeFormat();

  if (!isOpen) {
    return null;
  }

  const getAttribution = () => {
    if (action.version_notes) {
      if (action.version_notes.includes('Auto-versioned')) {
        return 'Manual edit';
      }
      if (action.version_notes.toLowerCase().includes('chat')) {
        return 'Via chat';
      }
      return action.version_notes;
    }
    return 'Manual edit';
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) {
      return 'Unknown date';
    }
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...timeParts(timeFormat),
    });
  };

  const handleRestore = () => {
    if (onRestore) {
      onRestore(action);
      onClose();
    }
  };

  // Render via portal to document.body so the modal escapes the proposal
  // panel's scroll container (which has its own stacking context). Without
  // this, `fixed inset-0` is trapped inside the actions space instead of
  // covering the viewport. Z-index sits above the version history sidebar
  // (z-[10001]) since the modal is opened from it.
  return createPortal(
    // StackingLevelProvider sets an explicit stacking level for everything
    // rendered inside this portal, so overlays opened from within it (e.g.
    // EmailPreviewModal via EmailActionPreview's Preview button) compute a
    // z-index above this modal's own hardcoded z-[10002] instead of falling
    // back to the ambient (root) stacking level.
    <StackingLevelProvider level={98}>
      <div className='fixed inset-0 bg-black bg-opacity-50 z-[10002] flex items-center justify-center p-4'>
        <div className='bg-primary rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col'>
          {/* Header */}
          <div className='px-6 py-4 border-b border-primary flex items-center justify-between'>
            <div>
              <h3 className='text-lg font-semibold text-primary flex items-center gap-2'>
                Version {action.version_number}
                {isLatest && (
                  <span className='px-2 py-0.5 bg-utility-brand-100 text-utility-brand-700 text-xs font-medium rounded'>
                    Current
                  </span>
                )}
              </h3>
              <p className='text-sm text-secondary mt-1'>
                {getAttribution()} · {formatDateTime(action.created_at)}
              </p>
            </div>
            <Button
              color='tertiary'
              size='md'
              onClick={onClose}
              aria-label='Close version preview'
              iconLeading={XClose}
            />
          </div>

          {/* Content */}
          <div className='flex-1 overflow-y-auto p-6'>
            <EmailActionPreview
              action={action}
              proposalId={proposalId}
              editable={false}
              orgId={orgId}
            />
          </div>

          {/* Footer */}
          <div className='px-6 py-4 border-t border-primary flex items-center justify-between'>
            <Button color='secondary' size='md' onClick={onClose}>
              Close
            </Button>
            {!isLatest && onRestore && (
              <Button
                color='primary'
                size='md'
                onClick={handleRestore}
                iconLeading={RefreshCcw02}
              >
                Restore as latest version
              </Button>
            )}
          </div>
        </div>
      </div>
    </StackingLevelProvider>,
    document.body
  );
}
